import { format, parseISO, isValid } from 'date-fns';

/**
 * Formats a date string or Date object into the specified format
 * 
 * @param {string|Date} date - The date to format
 * @param {string} formatString - The format string (default: 'yyyy-MM-dd')
 * @returns {string} The formatted date string
 */
export const formatDate = (date, formatString = 'yyyy-MM-dd') => {
  if (!date) return '';
  
  try {
    // Handle different input types
    let dateObj;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      // For ISO strings or simple date strings from PostgreSQL
      dateObj = parseISO(date);
    } else {
      return '';
    }
    
    // Check if date is valid before formatting
    if (!isValid(dateObj)) {
      console.warn(`Invalid date: ${date}`);
      return '';
    }
    
    // Format the date using date-fns
    return format(dateObj, formatString);
  } catch (error) {
    console.error(`Error formatting date (${date}):`, error);
    return '';
  }
};

/**
 * Parses a date string or Date object to a Date object
 * Properly handles both string dates from PostgreSQL and existing Date objects
 * 
 * @param {string|Date} dateInput - Date string in format 'YYYY-MM-DD' or Date object
 * @returns {Date|null} - Date object or null if invalid
 */
export const parseDate = (dateInput) => {
  if (!dateInput) return null;
  
  try {
    // If it's already a Date object, just validate it
    if (dateInput instanceof Date) {
      return isValid(dateInput) ? dateInput : null;
    }
    
    // For PostgreSQL DATE format: 'YYYY-MM-DD' as string
    if (typeof dateInput === 'string') {
      const parsedDate = parseISO(dateInput);
      return isValid(parsedDate) ? parsedDate : null;
    }
    
    // Unknown type, try converting to string and parsing
    const parsedDate = new Date(String(dateInput));
    return isValid(parsedDate) ? parsedDate : null;
  } catch (error) {
    console.error(`Error parsing date (${dateInput}):`, error);
    return null;
  }
};
